import React, { lazy, useEffect, useRef, useState } from "react";
import { doc, getDocFromServer } from "firebase/firestore";
import { db } from "../../../firebase";
import LeaderboardHeader from "./LeaderboardHeader";
import LeaderboardLoadingState from "./LeaderboardLoadingState";
import ChallengeMapSection, { hasChallengeMap } from "./ChallengeMapSection";
import { loadCompactMap, loadCompactPage, loadCurrentRank } from "./compactLeaderboardData";
import { getRaceSizeBucket, startLeaderboardLoadTrace } from "./leaderboardPerformance";

const LiveMap = lazy(() => import("./NewLeaderBoardMap"));

function CompactMap({ ownerData, mapName }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setError(false);
    loadCompactMap(ownerData.legId).then((result) => { if (active) setRows(result); })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [ownerData.legId, attempt]);
  if (error) return <div role="alert" className="p-4">Couldn’t load the map. <button onClick={() => setAttempt((n) => n + 1)}>Try again</button></div>;
  if (!rows) return <p role="status" className="p-4">Loading live map...</p>;
  return <LiveMap racersData={rows} ownerData={ownerData} mapName={mapName} dataSource="compact" />;
}

function LeaderboardRow({ row, rank, distance, own = false }) {
  const { data } = row;
  const progress = distance > 0 ? data.totalDistance * 100 / distance : 0;
  return <div className={`mb-3 rounded-lg border-b-2 px-3 py-3 ${own ? "bg-blue-50" : ""}`}>
    <p className="mb-2 font-semibold">#{rank} {data.firstName} {data.lastName}{" "}
      <span className="text-xs text-green-700">{data.isTeam ? `Team of ${data.membersCount}` : `#${data.bibNumber ?? ""}`}</span>
    </p>
    {data.completedDate != null && <p className="text-sm">{new Date(data.completedDate).toLocaleString()}</p>}
    <div className="flex justify-between text-sm"><span>{data.totalDistance.toFixed(2)} km</span><span>{progress >= 100 ? "Completed!" : `${progress.toFixed(2)}%`}</span></div>
    <div className="mt-2 h-3 overflow-hidden rounded bg-slate-100"><div className="h-full bg-green-200" style={{ width: `${Math.max(0, Math.min(100, progress))}%` }} /></div>
  </div>;
}

const liveData = { loadPage: loadCompactPage, loadRank: loadCurrentRank };

export default function CompactLeaderboard({ selectedRace, onFallback, initialRaceData = null, initialTrace = null, dataSource = liveData }) {
  const [page, setPage] = useState(null);
  const [own, setOwn] = useState(null);
  const [raceData, setRaceData] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState(false);
  const [ownVisible, setOwnVisible] = useState(false);
  const [pinHeight, setPinHeight] = useState(180);
  const ownRow = useRef(null);
  const pinnedPosition = useRef(null);
  const pending = useRef(false);
  const generation = useRef(0);
  const raceId = selectedRace.legId;
  useEffect(() => {
    const request = ++generation.current;
    pending.current = true;
    setPage(null); setOwn(null); setOwnVisible(false);
    setLoadingMore(false); setMoreError(false);
    const trace = initialTrace || startLeaderboardLoadTrace({ raceId, dataSource: "compact" });
    trace.putAttribute("data_source", "compact");
    const metadata = initialRaceData ? Promise.resolve({ exists: () => true, data: () => initialRaceData }) : getDocFromServer(doc(db, "races", raceId));
    Promise.all([dataSource.loadPage(raceId), dataSource.loadRank(raceId), metadata])
      .then(([result, current, race]) => {
        if (request !== generation.current) return;
        if (!race.exists()) throw new Error("Race unavailable");
        setPage({ ...result, rows: result.rows.map((row, index) => ({ ...row, rank: result.startRank + index })) });
        setOwn(current); setRaceData(race.data());
        pending.current = false;
        trace.putMetric("participant_docs", 0);
        trace.putMetric("compact_docs", result.documentReads);
        trace.putMetric("leaderboard_reads", result.documentReads + 1);
        trace.putMetric("leaderboard_rows", result.rows.length);
        trace.putAttribute("race_size", getRaceSizeBucket(current.totalEntries));
        trace.putAttribute("has_map", hasChallengeMap(race.data().map) ? "yes" : "no");
        trace.stop("success");
      }).catch(() => {
        if (request !== generation.current) return;
        pending.current = false;
        trace.stop("error");
        onFallback();
      });
    return () => { generation.current = request + 1; trace.stop("cancelled"); };
  }, [raceId, onFallback, initialRaceData, initialTrace, dataSource]);

  useEffect(() => {
    if (!own || !pinnedPosition.current) return;
    const element = pinnedPosition.current;
    const measure = () => setPinHeight(element.getBoundingClientRect().height);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [own]);

  useEffect(() => {
    setOwnVisible(false);
    if (!ownRow.current || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) => {
      setOwnVisible(entry.isIntersecting && entry.intersectionRatio >= 0.5);
    }, { threshold: [0, 0.5], rootMargin: `0px 0px -${pinHeight}px 0px` });
    observer.observe(ownRow.current);
    return () => observer.disconnect();
  }, [page, own?.row.id, pinHeight]);

  async function loadMore() {
    if (pending.current || !page?.hasNext) return;
    pending.current = true;
    setLoadingMore(true); setMoreError(false);
    const request = generation.current;
    try {
      const [result, current] = await Promise.all([dataSource.loadPage(raceId, page.cursor), dataSource.loadRank(raceId)]);
      if (request !== generation.current) return;
      // Live updates can move a row across the cursor. Keep a single copy.
      setPage((previous) => {
        const rows = new Map(previous.rows.map((row) => [row.id, row]));
        result.rows.forEach((row, index) => {
          if (!rows.has(row.id)) rows.set(row.id, { ...row, rank: result.startRank + index });
        });
        return { ...result, rows: Array.from(rows.values()) };
      });
      setOwn(current);
    } catch (error) {
      if (request === generation.current) {
        if (["functions/failed-precondition", "functions/permission-denied", "functions/unauthenticated"].includes(error.code)) onFallback();
        else setMoreError(true);
      }
    } finally {
      if (request === generation.current) { pending.current = false; setLoadingMore(false); }
    }
  }
  return <div className="relative mx-auto mt-5 w-full max-w-3xl" style={{ paddingBottom: own ? pinHeight + 24 : 0 }}>
    <LeaderboardHeader selectedRace={selectedRace} />
    {!page ? <LeaderboardLoadingState /> : <>
      <ChallengeMapSection key={raceId} ownerData={selectedRace} mapName={raceData?.map} MapComponent={CompactMap} />
      <section aria-label="Leaderboard results">
        {page.rows.map((row) => <div key={row.id} ref={row.id === own.row.id ? ownRow : null}>
          <LeaderboardRow row={row} rank={row.rank} distance={Number(selectedRace.raceInfo.distance)} own={row.id === own.row.id} />
        </div>)}
      </section>
      <div className="my-5 text-center">
        {moreError && <p role="alert" className="text-sm text-red-700">Couldn’t load more results. Please try again.</p>}
        {page.hasNext && <button type="button" className="rounded-lg border border-slate-300 bg-white px-6 py-3 font-semibold disabled:opacity-50" disabled={loadingMore} onClick={loadMore}>
          {loadingMore ? "Loading more…" : moreError ? "Try again" : "Load more"}
        </button>}
        <p role="status" className="mt-3 text-sm text-slate-500">{page.rows.length} results loaded</p>
      </div>
      <aside ref={pinnedPosition} aria-label="Your leaderboard position" aria-hidden={ownVisible}
        className="fixed bottom-0 left-1/2 z-30 w-full max-w-3xl -translate-x-1/2 border-t border-blue-100 bg-white/95 px-3 pt-3 shadow-lg backdrop-blur-md"
        style={{ visibility: ownVisible ? "hidden" : "visible", paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}>
        <h2 className="mb-2 !text-sm !font-semibold !leading-5">Your position · {own.rank} of {own.totalEntries}</h2>
        <LeaderboardRow row={own.row} rank={own.rank} distance={Number(selectedRace.raceInfo.distance)} own />
      </aside>
    </>}
  </div>;
}
